import { Box, Grid } from "@mui/material";
import * as React from "react";
import { SaveDataObj } from "source/framework/saveData";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as recordService from "source/models/services/recordService";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { IRecordUserSettings } from "source/models/record/recordUserSettings";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";

export interface IRecordSettingsProp {
  classes?: string;
  companyId: string;
  onOk: (closeDialog: boolean) => void;
  onCancel: (closeDialog: boolean) => void;
}

export interface IRecordSettingsState {
  userFurtherEducationArhiveExpiredDurationInMonths: number;
  item: IRecordUserSettings;
  rowId: string;
}

export type TRecordSettingsRefProps = {
    cancel: (closeDialog: boolean) => Promise<boolean>;
};

export const RecordSettings = forwardRef<TRecordSettingsRefProps, IRecordSettingsProp>((props, ref) => {
    const [state, setState] = React.useState<IRecordSettingsState>({
        userFurtherEducationArhiveExpiredDurationInMonths: -1,
        item: null,
        rowId: ""
    });
    const orgStateHash = React.useRef("");

    const getSaveDataFromState = (aState: IRecordSettingsState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("rowId", aState.rowId);
        result.append("userFurtherEducationArhiveExpiredDurationInMonths", aState.userFurtherEducationArhiveExpiredDurationInMonths);
        return result;
    };

    const loadEventSettingsRecordUserSettings = async () => {
        const result = await AppUtils.callService(
            () => recordService.getRecordUserSettings(props.companyId),
            false
        );
        if (!result) return;
        const newState = {
            ...state,
            item: result,
            rowId: result.rowId,
            userFurtherEducationArhiveExpiredDurationInMonths: result.userFurtherEducationArhiveExpiredDurationInMonths
        };
        orgStateHash.current = getSaveDataFromState(newState).hash;
        setState(newState);
    };

    useEffect(() => {
        loadEventSettingsRecordUserSettings();
    }, []);

    const saveToDb = async (): Promise<boolean> => {
        const saveData = getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() =>
            recordService.saveRecordUserSettings(saveData.obj)
        );
        if (!result) return false;
        const newState = {
            ...state,
            rowId: result.rowId
        };
        orgStateHash.current = getSaveDataFromState(newState).hash;
        setState((old) => ({
            ...old,
            rowId: result.rowId
        }));
        return true;
    };

    const handleSave = async (closeDialog: boolean) => {
        if (!(await saveToDb())) return;
        props.onOk(closeDialog);
    };

    const handleCancel = async (closeDialog: boolean) => {
        const cancelResult = await AppUtils.cancel(getSaveDataFromState(state).hash, orgStateHash.current, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            handleSave(closeDialog);
        } else {
            props.onCancel(closeDialog);
        }
        return true;
    };

    useImperativeHandle(ref, () => {
        return {
            save() {
                return saveToDb();
            },
            cancel(closeDialog: boolean) {
                return handleCancel(closeDialog);
            }
        };
    }, [orgStateHash.current, state]);
        
    if (!state.item) return null;

    return (
        <Box display="flex" flexDirection="column" flexGrow={2} justifyContent="space-between">
            <DialogSettingsCard
                fitToContainerHeight
                title={Translations.RecordUserSettings}
                //info={props.companyId ? Translations.EventModuleInfoSettings : undefined}
                cardContent={
                    <>
                        <GridRow>
                            <Grid item sm={12} md={6}>
                                <FormField title={Translations.UserFurtherEducationArhiveExpiredDurationInMonths}>
                                    <IdTitleDropdown
                                        selectedId={state.userFurtherEducationArhiveExpiredDurationInMonths.toString(10)}
                                        items={[
                                            { id: "-1", title: Translations.NotInUse },
                                            { id: "0", title: Translations.ImmediatelyAfterExpiration },
                                            { id: "1", title: String.format(Translations.ParameterMonthsAfterExpiration, "1") },
                                            { id: "2", title: String.format(Translations.ParameterMonthsAfterExpiration, "2") },
                                            { id: "3", title: String.format(Translations.ParameterMonthsAfterExpiration, "3") },
                                            { id: "4", title: String.format(Translations.ParameterMonthsAfterExpiration, "4") },
                                            { id: "5", title: String.format(Translations.ParameterMonthsAfterExpiration, "5") },
                                            { id: "6", title: String.format(Translations.ParameterMonthsAfterExpiration, "6") },
                                            { id: "7", title: String.format(Translations.ParameterMonthsAfterExpiration, "7") },
                                            { id: "8", title: String.format(Translations.ParameterMonthsAfterExpiration, "8") },
                                            { id: "9", title: String.format(Translations.ParameterMonthsAfterExpiration, "9") },
                                            { id: "10", title: String.format(Translations.ParameterMonthsAfterExpiration, "10") },
                                            { id: "11", title: String.format(Translations.ParameterMonthsAfterExpiration, "11") },
                                            { id: "12", title: String.format(Translations.ParameterMonthsAfterExpiration, "12") },
                                        ]}
                                        onChange={(id) => {
                                            setState((old) => ({
                                                ...old,
                                                userFurtherEducationArhiveExpiredDurationInMonths: id.toInteger()
                                            }));
                                        }}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                    </>
                }
            />
            <Box display="flex" justifyContent="flex-end" paddingTop="1.9rem" sx={{ width: "100%" }}>
                <DialogButtons
                    onCancel={() => handleCancel(true)}
                    onSave={() => handleSave(false)}
                    saveDisabled={getSaveDataFromState(state).hash === orgStateHash.current}
                />
            </Box>
        </Box>
    );
});

RecordSettings.displayName = "RecordSettings";
